import * as React from 'react';
import {assignProps} from '@sail/engine';
import {message, useIntl} from '@sail/intl';

import {SelectField} from '../../SelectField';
import {PopoverConfig} from '../../popover/PopoverConfig';
import {css} from '../../../css';

export type CurrencySelectorProps = {
  compositeFieldRef: React.RefObject<HTMLDivElement>;
  currencyData: {
    items: Array<{id: string; label: string}>;
    disabledItems: string[];
  };
  currencySelectLabel?: string;
  defaultFilter: <T>(textValue: string, inputValue: string, item: T) => boolean;
  disabled?: boolean;
  getItemDescription: (code: string) => string;
  hasError: boolean;
  internalCurrency: string;
  onSelectionChange: (key: string | number) => void;
  readOnly?: boolean;
  size: 'small' | 'medium' | 'large';
  symbolPosition: 'start' | 'end';
};

const currencySelectorFieldStyle = css({
  minWidth: 'fit',
});

const selectButtonBaseStyle = css({
  border: 'none',
  keyline: 'none',
  topShadow: 'none',
});

const selectButtonErrorHoverStyle = css({
  ':hover:not(:active):not([aria-disabled="true"])': {
    keyline: 'form.error',
  },
});

const selectButtonFlattenLeftStyle = css({
  borderTopLeftRadius: 0,
  borderBottomLeftRadius: 0,
});

const selectButtonFlattenRightStyle = css({
  borderTopRightRadius: 0,
  borderBottomRightRadius: 0,
});

const currencySelectDefaultLabel = message({
  id: '@sail/ui.currencyField.currencySelectLabel',
  description: 'The default label for the currency selection dropdown',
  defaultMessage: 'Currency',
});

/* @internal */
export const CurrencySelector = ({
  compositeFieldRef,
  currencyData,
  currencySelectLabel,
  defaultFilter,
  disabled,
  getItemDescription,
  hasError,
  internalCurrency,
  onSelectionChange,
  readOnly,
  size,
  symbolPosition,
}: CurrencySelectorProps) => {
  const intl = useIntl();

  const subViews = React.useMemo(() => {
    const buttonCss = [selectButtonBaseStyle];

    if (hasError) {
      buttonCss.push(selectButtonErrorHoverStyle);
    }

    if (symbolPosition !== 'end') {
      buttonCss.push(selectButtonFlattenLeftStyle);
    } else {
      buttonCss.push(selectButtonFlattenRightStyle);
    }

    return {
      button: {css: buttonCss},
    };
  }, [hasError, symbolPosition]);

  return (
    <SelectField
      onSelectionChange={onSelectionChange}
      selectedKey={internalCurrency}
      disabledKeys={currencyData.disabledItems}
      readOnly={readOnly}
      disabled={disabled}
      filterable
      label={
        currencySelectLabel ?? intl.formatMessage(currencySelectDefaultLabel)
      }
      css={currencySelectorFieldStyle}
      size={size}
      subviews={subViews}
      uses={[
        PopoverConfig.customize({
          uses: [assignProps({targetRef: compositeFieldRef})],
        }),
      ]}
      data={currencyData.items}
      item={{
        textValue: (item) => (item as {label: string}).label,
        description: (item) => getItemDescription((item as {id: string}).id),
      }}
      defaultFilter={defaultFilter}
    />
  );
};
